// Admin · Users view. Reads /admin/users; mutations wired to the real
// /admin/users* endpoints via api/admin-mutations.ts. Sort/filter operate on
// canonical User fields (ISO timestamps; lowercase enum roles + statuses).
//
// After invite / resend-invite / reset-password the server returns a one-time
// `action_link`; the flash banner surfaces it with a copy button — the product
// fallback when email delivery isn't configured.
//
// Role changes, disable, re-enable and resend keep the user drawer open and say
// what happened in its status line; reset-password and delete still close it
// (the reset link is on the page banner, a deleted user has nothing to show).

import { ResponsiveFilters } from '@/components/responsive-filters';
import { useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Icons } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import { useAdminUsers } from '@/api/hooks';
import {
  usePatchUser, useDisableUser, useEnableUser,
  useResendInvite, useIssuePasswordReset, useDeleteUser,
} from '@/api/admin-mutations';
import { useMe } from '@/auth/useMe';
import { Avatar, RoleBadge, UserStatus } from './admin-shared';
import { NewUserModal, UserDetailDrawer, fmtRelative, inviterName, ROLE_LABEL, STATUS_LABEL } from './admin-user-drawer';
import { PageActions } from '@/components/page-actions';
import { csvFilename, downloadCsv } from '@/lib/csv';
import { isoUtc } from '@/lib/datetime';
import type { Role, User, UserStatus as UserStatusValue } from '@/api/types';

type FlashKind = 'invite' | 'resend' | 'reset';

/** Set on a successful invite / resend / reset; `link` is the one-time action link. */
interface Flash {
  kind: FlashKind;
  email: string;
  link?: string;
}

type RoleFilter = 'all' | Role;
type StatusFilter = 'all' | UserStatusValue;
type UserSort = 'recent' | 'name' | 'role';

/**
 * The drawer's user. `savedAt` is when a drawer action returned it (0 when it
 * was opened from the list): until the list has refetched after that instant,
 * the action's response is newer than the list row, so the drawer shows it.
 */
interface Selection {
  user: User;
  savedAt: number;
}

const FLASH_HEADLINE: Record<FlashKind, (email: string) => ReactNode> = {
  invite: (email) => <>Invite sent to <b>{email}</b>.</>,
  resend: (email) => <>Invite re-sent to <b>{email}</b> — the old link no longer works.</>,
  reset:  (email) => <>Password reset issued for <b>{email}</b>.</>,
};

// Flash banner after invite / resend / reset. Shows the returned one-time
// action link with copy-to-clipboard so the admin can deliver it by hand.
interface ActionFlashProps {
  flash: Flash;
  onClose: () => void;
}

function ActionFlash({ flash, onClose }: ActionFlashProps) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!flash.link) return;
    try {
      await navigator.clipboard.writeText(flash.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions/non-secure context) — the link
      // stays visible below for manual selection.
    }
  };
  return (
    <div style={{margin:'12px 22px 0', padding:'10px 14px', background:'color-mix(in oklch, var(--good) 12%, var(--bg-elev))', border:'1px solid color-mix(in oklch, var(--good) 40%, var(--line))', borderRadius:'var(--r)'}}>
      <div style={{display:'flex', alignItems:'center', gap:10}}>
        <span style={{color:'var(--good)', fontSize:14}}>{Icons.check}</span>
        <span style={{fontSize:12.5}}>{FLASH_HEADLINE[flash.kind](flash.email)}</span>
        <button className="btn btn-ghost btn-sm" aria-label="Close" style={{marginLeft:'auto', padding:'2px 8px'}} onClick={onClose}>{Icons.x}</button>
      </div>
      {flash.link && (
        <div style={{marginTop:8, paddingTop:8, borderTop:'1px solid var(--line-2)'}}>
          <div className="muted" style={{fontSize:11.5, marginBottom:6}}>
            Emailed if delivery is configured — or copy this link and send it yourself.
            Invites expire in 7 days; reset links in 1 hour.
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <code className="mono" style={{flex:'1 1 0', minWidth:0, fontSize:11, padding:'6px 8px', background:'var(--bg-sunk)', border:'1px solid var(--line)', borderRadius:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
              {flash.link}
            </code>
            <button className="btn btn-sm" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const ROW_H = 44;
const { Table } = createDataTable([
  { key: 'user',     header: 'User',      width: 280, mobile: 'title' },
  { key: 'role',     header: 'Role',      width: 130, mobile: 'stat' },
  { key: 'status',   header: 'Status',    width: 130, mobile: 'status' },
  { key: 'lastseen', header: 'Last sign-in', width: 130, mobile: 'stat' },
  { key: 'added',    header: 'Added',     width: 130, priority: 3 },
  { key: 'security', header: 'Security',  width: 100, priority: 2 },
]);

export interface UsersViewProps {
  setView?: (viewId: string) => void;
}

function UsersView({ setView: _setView }: UsersViewProps) {
  const usersQuery = useAdminUsers();
  const users = usersQuery.data ?? [];
  const { data: me } = useMe();
  const myId = me?.id;

  const [q, setQ] = useState('');
  const [role, setRole] = useState<RoleFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<UserSort>('recent');
  const [selected, setSelected] = useState<Selection | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [flash, setFlash] = useState<Flash | null>(null);

  const patchUser = usePatchUser();
  const disableUser = useDisableUser();
  const enableUser = useEnableUser();
  const resendInvite = useResendInvite();
  const issueReset = useIssuePasswordReset();
  const deleteUser = useDeleteUser();
  const drawerMutations = [patchUser, disableUser, enableUser, resendInvite, issueReset, deleteUser];
  const drawerBusy = drawerMutations.some(m => m.isPending);
  const drawerError = drawerMutations.find(m => m.error)?.error ?? null;

  // The user whose drawer is open right now, for responses that land after a
  // close: a late success must not reopen a drawer the operator dismissed.
  const openId = useRef<string | null>(null);

  const openUser = (u: User) => {
    // Clear any stale error/pending flags and the last action's sentence from
    // the previous drawer session.
    drawerMutations.forEach(m => m.reset());
    setNotice(null);
    openId.current = u.id;
    setSelected({ user: u, savedAt: 0 });
  };
  const closeUser = () => {
    openId.current = null;
    setSelected(null);
    setNotice(null);
  };
  // A new action replaces the last one's outcome: its sentence and any error
  // another mutation left behind.
  const startAction = () => {
    drawerMutations.forEach(m => m.reset());
    setNotice(null);
  };
  // The drawer stays open on the user the server returned, with the sentence.
  const showSaved = (user: User, sentence: string) => {
    if (openId.current !== user.id) return;
    setSelected({ user, savedAt: Date.now() });
    setNotice(sentence);
  };

  const listRow = selected ? users.find(u => u.id === selected.user.id) : undefined;
  const drawerUser = selected
    ? (listRow && usersQuery.dataUpdatedAt > selected.savedAt ? listRow : selected.user)
    : null;

  const list = useMemo(() => {
    let out = users;
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      out = out.filter(u =>
        u.name.toLowerCase().includes(n) ||
        u.email.toLowerCase().includes(n)
      );
    }
    if (role !== 'all')   out = out.filter(u => u.role === role);
    if (status !== 'all') out = out.filter(u => u.status === status);
    out = out.slice();
    if (sort === 'name')   out.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'role')   out.sort((a, b) => a.role.localeCompare(b.role));
    if (sort === 'recent') out.sort((a, b) => +new Date(b.last_seen || 0) - +new Date(a.last_seen || 0));
    return out;
  }, [users, q, role, status, sort]);

  // `useAdminUsers` reads every page of the directory (and fails rather than
  // stop early), so `list` is every user the filters match: nothing is cut.
  const exportCsv = () => {
    downloadCsv(csvFilename('users'), list.map(u => ({
      'Name': u.name,
      'Email': u.email,
      'Role': ROLE_LABEL[u.role],
      'Status': STATUS_LABEL[u.status],
      'Invited': isoUtc(u.invited_at),
      'Invited by': inviterName(u.invited_by, users) ?? '',
      'Last sign-in': isoUtc(u.last_seen),
    })));
  };

  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Users</h1>
        </div>
        <PageActions>
          <button
            className="btn"
            disabled={list.length === 0}
            title={`Download the ${list.length} user${list.length === 1 ? '' : 's'} shown as CSV`}
            onClick={exportCsv}
          ><span>{Icons.dl}</span>Export</button>
          <button className="btn btn-primary" data-phone="inline" onClick={() => setInviteOpen(true)}>
            <span>{Icons.send}</span><span className="phone-long">Invite user</span><span className="phone-short">Invite</span>
          </button>
        </PageActions>
      </div>

      {flash && <ActionFlash flash={flash} onClose={() => setFlash(null)} />}

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0', minWidth: 240}}>
          <span style={{color:'var(--ink-3)'}}>{Icons.search}</span>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter users by name or email…"/>
        </div>
        <Dropdown label="ROLE" value={role} options={[
          { id: 'all',      label: 'Any role' },
          { id: 'admin',    label: ROLE_LABEL.admin,    dot: 'var(--bad)' },
          { id: 'planner',  label: ROLE_LABEL.planner,  dot: 'var(--accent)' },
          { id: 'reporter', label: ROLE_LABEL.reporter, dot: 'var(--chart-3)' },
        ]} onChange={setRole} width={170}/>
        <Dropdown label="STATUS" value={status} options={[
          { id: 'all',      label: 'Any status' },
          { id: 'active',   label: STATUS_LABEL.active,   dot: 'var(--good)' },
          { id: 'invited',  label: STATUS_LABEL.invited,  dot: 'var(--warn)' },
          { id: 'disabled', label: STATUS_LABEL.disabled, dot: 'var(--ink-3)' },
        ]} onChange={setStatus} width={170}/>
        <Dropdown label="SORT" value={sort} options={[
          { id: 'recent', label: 'Most recent sign-in' },
          { id: 'name',   label: 'Name · A → Z' },
          { id: 'role',   label: 'Role' },
        ]} onChange={setSort} width={210} rightAlign/>
      </ResponsiveFilters>

      <Table
        rows={list}
        rowH={ROW_H}
        unfilteredTotal={users.length}
        deps={[{ what: 'users', query: usersQuery }]}
        empty="No users yet."
        noMatch="No users match these filters."
        onRowClick={openUser}
        rowClassName={(u) => (u.status === 'disabled' ? 'row-mute' : undefined)}
        renderRow={(u) => {
          const isYou = u.id === myId;
          return (
            <>
              <td className="name">
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Avatar user={u}/>
                  <div style={{minWidth:0}}>
                    <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                      <span style={{fontWeight:600, color:'var(--ink)'}}>{u.name}</span>
                      {isYou && <span className="tag" style={{fontSize:9, padding:'1px 5px'}}>YOU</span>}
                    </div>
                    <div className="muted mono" style={{fontSize:10.5, marginTop:1, letterSpacing:'.02em'}}>{u.email}</div>
                  </div>
                </div>
              </td>
              <td><RoleBadge role={u.role}/></td>
              <td><UserStatus status={u.status}/></td>
              <td className="muted">{u.last_seen ? fmtRelative(+new Date(u.last_seen)) : '—'}</td>
              <td className="muted">{u.invited_at ? fmtRelative(+new Date(u.invited_at)) : '—'}</td>
              <td>
                <div style={{display:'flex',gap:6}}>
                  {u.twofa && <span className="tag" title="Two-factor auth enabled" style={{background:'color-mix(in oklch, var(--good) 12%, transparent)', color:'var(--good)', borderColor:'color-mix(in oklch, var(--good) 35%, var(--line))'}}>2FA</span>}
                  {u.sso && <span className="tag" title="Sign-in via SSO">SSO</span>}
                </div>
              </td>
            </>
          );
        }}
      />

      {selected && drawerUser && (
        <UserDetailDrawer
          key={selected.user.id}
          user={drawerUser}
          users={users}
          busy={drawerBusy}
          error={drawerError}
          notice={notice}
          onClose={closeUser}
          onSaveRole={(uid, newRole) => {
            startAction();
            patchUser.mutate({ id: uid, role: newRole }, {
              onSuccess: (r) => showSaved(r.user, `Role changed to ${ROLE_LABEL[r.user.role]}.`),
            });
          }}
          onDisable={(u) => {
            startAction();
            disableUser.mutate(u.id, {
              onSuccess: (r) => showSaved(r.user, 'Disabled. They can’t sign in until re-enabled.'),
            });
          }}
          onEnable={(u) => {
            startAction();
            enableUser.mutate(u.id, {
              onSuccess: (r) => showSaved(r.user, 'Re-enabled. They can sign in again.'),
            });
          }}
          onResend={(u) => {
            startAction();
            resendInvite.mutate(u.id, {
              onSuccess: (r) => {
                // The one-time link waits on the page banner, behind the drawer.
                showSaved(r.user, r.action_link
                  ? `Invite sent again to ${r.user.email}. Close this panel to copy the new link.`
                  : `Invite sent again to ${r.user.email}.`);
                setFlash({ kind: 'resend', email: r.user.email, link: r.action_link });
              },
            });
          }}
          onResetPassword={(u) => {
            issueReset.mutate(u.id, {
              onSuccess: (r) => {
                closeUser();
                setFlash({ kind: 'reset', email: u.email, link: r.action_link });
              },
            });
          }}
          onDelete={(u) => {
            deleteUser.mutate(u.id, { onSuccess: closeUser });
          }}
        />
      )}

      <NewUserModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(r) => {
          setInviteOpen(false);
          setFlash({ kind: 'invite', email: r.user.email, link: r.action_link });
        }}
        existing={users}
      />
    </div>
  );
}

export { UsersView };
export { Avatar, RoleBadge } from './admin-shared';
